import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { showSuccessUpdateAlert, showError } from '../../../../core/alert/sweetAlertConfig';

interface LocationDTO {
  id: number;
  name: string;
  address: string;
  lat: number;
  lng: number;
  description?: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string | null;
  updatedAt?: string | null;
}

interface BuildingDTO {
  id: number;
  name: string;
  noOfFloors?: number;
  locationName?: string;
  locationId?: number;
}

interface UpdateBuildingModuleProps {
  show: boolean;
  onHide: () => void;
  building: BuildingDTO | null;
  onSuccess: () => void;
  locations: LocationDTO[];        // <-- new prop
}

type FormState = {
  id: string;
  name: string;
  noOfFloors: string;
  locationId: string;              // keep as string for the select; cast to number on submit
};

const emptyForm: FormState = {
  id: '',
  name: '',
  noOfFloors: '',
  locationId: '',
};

const UpdateBuildingModule: React.FC<UpdateBuildingModuleProps> = ({
  show,
  onHide,
  building,
  onSuccess,
  locations,                         // <-- receive locations
}) => {
  const [formData, setFormData] = useState<FormState>(emptyForm);
  const [isLoading, setIsLoading] = useState(false);

  // Choose a safe initial locationId if building's is missing
  const defaultLocationId = useMemo(() => {
    if (building?.locationId) return String(building.locationId);
    if (locations.length > 0) return String(locations[0].id);
    return '';
  }, [building?.locationId, locations]);

  // Initialize / update form when building or locations change
  useEffect(() => {
    if (!building) {
      setFormData(emptyForm);
      return;
    }
    setFormData({
      id: String(building.id ?? ''),
      name: building.name ?? '',
      noOfFloors: String(building.noOfFloors ?? ''),
      locationId: defaultLocationId,        // <-- init to building’s or first location
    });
  }, [building, defaultLocationId]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Validation
  const hasId = formData.id.trim().length > 0;
  const hasName = formData.name.trim().length > 0;
  const hasFloors = formData.noOfFloors.trim().length > 0;   // '0' allowed
  const hasLocationId = formData.locationId.trim().length > 0;

  const isDisabled =
    isLoading || !hasId || !hasName || !hasFloors || !hasLocationId || locations.length === 0;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isDisabled) {
      if (!hasName) return showError('Please enter a building name.');
      if (!hasFloors) return showError('Please enter number of floors.');
      if (!hasLocationId) return showError('Please select a location.');
      if (locations.length === 0) return showError('No locations available. Please add a location first.');
      return;
    }

    const floorsNum = Number(formData.noOfFloors);
    if (!Number.isFinite(floorsNum) || floorsNum < 0) {
      return showError('Number of floors must be 0 or greater.');
    }

    setIsLoading(true);
    try {
      const payload = {
        id: Number(formData.id),
        name: formData.name.trim(),
        noOfFloors: floorsNum,
        locationId: Number(formData.locationId),  // <-- send the selected location
      };

      const res = await apiDefinitions.updateBuilding(payload);
      if (res.status === 200) {
        showSuccessUpdateAlert("Building updated successfully");
        onSuccess();
        onHide();
      } else {
        showError(res.data?.statusDescription || 'Failed to update building.');
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        showError('Unauthorized: Invalid or expired token. Please log in again.');
      } else if (err.response?.status === 400) {
        showError(err.response?.data?.statusDescription || 'Invalid building data.');
      } else if (err.response?.status === 409) {
        showError('Building name already exists at this location.');
      } else {
        showError('Failed to update building. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!building) return null;

  return (
    <Modal show={show} onHide={() => !isLoading && onHide()} centered backdrop="static" keyboard={!isLoading}>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Update Building: {building.name ?? ''}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group className="mb-3" controlId="buildingId">
            <Form.Label>ID</Form.Label>
            <Form.Control type="text" name="id" value={formData.id} disabled />
          </Form.Group>

          <Form.Group className="mb-3" controlId="buildingName">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter building name"
              disabled={isLoading}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="noOfFloors">
            <Form.Label>No. of Floors</Form.Label>
            <Form.Control
              type="number"
              name="noOfFloors"
              value={formData.noOfFloors}
              onChange={handleInputChange}
              placeholder="Enter number of floors"
              min="0"
              disabled={isLoading}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="locationId">
            <Form.Label>Location</Form.Label>
            {locations.length > 0 ? (
              <Form.Select
                name="locationId"
                value={formData.locationId}
                onChange={handleInputChange}
                disabled={isLoading}
                required
              >
                <option value="" disabled>
                  Select a location
                </option>
                {locations.map(loc => (
                  <option key={loc.id} value={String(loc.id)}>
                    {loc.name} {/* you can include address if you like */}
                  </option>
                ))}
              </Form.Select>
            ) : (
              <Form.Control type="text" value="No locations found" disabled />
            )}
            {/* Hidden is no longer necessary since we use a select, but harmless if you want to keep */}
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="primary" onClick={onHide} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="secondary" type="submit" disabled={isDisabled}>
            {isLoading ? 'Updating...' : 'Update Building'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default UpdateBuildingModule;
